// Accur8-specific log parser
// Format: timestamp | level | thread_name | [extra_meta] | category | message
// Example: 09:45:12.380 | INFO  | main | [] | a8.qubes.main.ActiveDirectoryUserImport | Ignoring unchanged user...

import { LogEntry, LogLevel, ParseLineResult } from './types';

// Support both Accur8 format and standard Log4j format:
// Accur8: HH:MM:SS.mmm | LEVEL | thread | [metadata] | category | message
// Log4j:  HH:MM:SS,mmm | thread |  | LEVEL | category | message
// Make metadata field optional (can be empty, [], or [data])
const ACCUR8_LOG_PATTERN = /^(\d{2}):(\d{2}):(\d{2})[.,](\d{3})\s*\|\s*(\w+)\s*\|\s*([^|]*)\s*\|\s*(\[.*?\]|)\s*\|\s*([^|]+)\s*\|\s*(.*)$/;

let logIdCounter = 0;
let baseDate: Date | null = null;

/**
 * Parse a line in Accur8 format
 *
 * Format: timestamp | level | thread_name | [extra_meta] | category | message
 *
 * If the line doesn't match this format, it's treated as a continuation
 * of the previous log entry.
 */

export function parseAccur8LogLine(
  rawLine: string,
  sourceId?: string,
  blockTimestamp?: Date
): ParseLineResult {
  const match = rawLine.match(ACCUR8_LOG_PATTERN);

  if (!match) {
    // This line doesn't match the pattern - it's a continuation
    return {
      isContinuation: true,
      continuationText: rawLine,
    };
  }

  // Extract parsed components - now includes hour/minute/second/ms separately
  const [
    ,
    hours,
    minutes,
    seconds,
    milliseconds,
    levelStr,
    threadName,
    extraMetaStr,
    category,
    message,
  ] = match;

  // Determine base date for timestamp construction
  let refDate: Date;
  if (blockTimestamp) {
    // Use SSE block timestamp as reference
    refDate = blockTimestamp;
  } else {
    // Fallback to cached date (for backwards compatibility)
    if (!baseDate) {
      baseDate = new Date();
    }
    refDate = baseDate;
  }

  // Parse time components
  const logHour = parseInt(hours, 10);
  const logMinute = parseInt(minutes, 10);
  const logSecond = parseInt(seconds, 10);
  const logMillis = parseInt(milliseconds, 10);

  // Detect midnight wraparound:
  // If log time is late (>= 22:00) and block time is early (< 02:00),
  // the log is likely from the previous day
  const adjustedDate = new Date(refDate);
  if (blockTimestamp) {
    const blockHour = blockTimestamp.getHours();
    if (logHour >= 22 && blockHour < 2) {
      // Log is from previous day
      adjustedDate.setDate(adjustedDate.getDate() - 1);
    }
  }

  const timestamp = new Date(
    adjustedDate.getFullYear(),
    adjustedDate.getMonth(),
    adjustedDate.getDate(),
    logHour,
    logMinute,
    logSecond,
    logMillis
  );

  // Parse level - avoid trim if possible
  const level = levelStr.toUpperCase() as LogLevel;

  // Parse extra metadata only if it looks like JSON
  let extraMeta: unknown[] | undefined;
  if (extraMetaStr && extraMetaStr.length > 2) {
    try {
      extraMeta = JSON.parse(extraMetaStr);
      if (extraMeta!.length === 0) extraMeta = undefined;
    } catch (_e) {
      // If parsing fails, just store as string
      extraMeta = [extraMetaStr];
    }
  }

  // Create log entry - use simpler ID without Date.now()
  const entry: LogEntry = {
    id: `${sourceId || 'default'}-${++logIdCounter}`,
    timestamp,
    originalLine: rawLine,
    message,
    category,
    thread: threadName,
    level,
    source: sourceId,
    metadata: extraMeta ? { extraMeta } : undefined,
    isMultiLine: false, // Will be set to true if continuations are added
  };

  return {
    entry,
  };
}

/**
 * Create a stateful parser that handles multi-line log entries
 *
 * This returns a function that maintains state of the current log entry
 * and appends continuation lines to it.
 */
export function createAccur8Parser(sourceId?: string) {
  let currentEntry: LogEntry | null = null;

  return {
    /**
     * Parse a line and return any completed entries
     */
    parseLine(rawLine: string, blockTimestamp?: Date): LogEntry[] {
      const result = parseAccur8LogLine(rawLine, sourceId, blockTimestamp);

      if (result.isContinuation && result.continuationText) {
        // This is a continuation line
        if (currentEntry) {
          // We have a current entry - attach this as a continuation
          const continuationEntry: LogEntry = {
            id: `${sourceId || 'default'}-${++logIdCounter}`,
            timestamp: currentEntry.timestamp,
            originalLine: rawLine,
            message: result.continuationText,
            category: currentEntry.category,
            thread: currentEntry.thread,
            level: currentEntry.level,
            source: sourceId,
            metadata: { continuation: true },
            isMultiLine: false,
          };

          // Initialize continuationLines array if needed
          if (!currentEntry.continuationLines) {
            currentEntry.continuationLines = [];
          }
          currentEntry.continuationLines.push(continuationEntry);
          currentEntry.isMultiLine = true;
          return []; // No completed entries yet
        } else {
          // No current entry - treat this as a plain text line
          const plainTextEntry: LogEntry = {
            id: `${sourceId || 'default'}-${++logIdCounter}`,
            timestamp: blockTimestamp || new Date(),
            originalLine: rawLine,
            message: result.continuationText,
            category: undefined,
            thread: undefined,
            level: undefined,
            source: sourceId,
            isMultiLine: false,
          };
          return [plainTextEntry];
        }
      }

      // This is a new log entry
      const newEntry = result.entry!;
      const completed: LogEntry[] = [];

      // If we had a previous entry, it's now complete
      if (currentEntry) {
        completed.push(currentEntry);
      }

      // Set this as the new current entry
      currentEntry = newEntry;

      return completed;
    },

    /**
     * Flush any pending entry (call this when stream ends)
     */
    flush(): LogEntry[] {
      if (currentEntry) {
        const entry = currentEntry;
        currentEntry = null;
        return [entry];
      }
      return [];
    },

    /**
     * Reset parser state
     */
    reset() {
      currentEntry = null;
    },
  };
}
